/**
 * Re-attach legacy FullCalendar v6 class names via v7 Class hooks so existing
 * Schedule / GameMaster SCSS (targeting .fc-timeline-*, .fc-datagrid-*, etc.)
 * continues to apply after the v7 theme/DOM rewrite.
 */
export const joinFcClass = (
	...parts: Array<string | false | null | undefined>
): string => parts.filter(Boolean).join(' ');

const slotHeaderClass = (data: {
	isMajor?: boolean;
	isMinor?: boolean;
	isTime?: boolean;
	level?: number;
}): string =>
	joinFcClass(
		'fc-timeline-slot',
		'fc-timeline-slot-label',
		'fc-col-header-cell',
		data?.level === 0 ? 'fc-timeline-slot-hour' : 'fc-timeline-slot-day',
		data?.isMajor && 'fc-timeline-slot-major',
		data?.isMinor && 'fc-timeline-slot-minor',
		data?.isTime && 'fc-timeline-header-row-chrono'
	);

const slotLaneClass = (data: { isMajor?: boolean; isMinor?: boolean }): string =>
	joinFcClass(
		'fc-timeline-slot',
		'fc-timeline-slot-lane',
		data?.isMajor && 'fc-timeline-slot-major',
		data?.isMinor && 'fc-timeline-slot-minor'
	);

const rowEventClass = (data: {
	isStart?: boolean;
	isEnd?: boolean;
	isMirror?: boolean;
	isDragging?: boolean;
	isResizing?: boolean;
	isSelected?: boolean;
	isDraggable?: boolean;
	isStartResizable?: boolean;
	isEndResizable?: boolean;
	event?: { extendedProps?: Record<string, unknown> };
}): string =>
	joinFcClass(
		'fc-event',
		'fc-h-event',
		'fc-timeline-event',
		data?.isStart && 'fc-event-start',
		data?.isEnd && 'fc-event-end',
		data?.isMirror && 'fc-event-mirror',
		data?.isDragging && 'fc-event-dragging',
		data?.isResizing && 'fc-event-resizing',
		data?.isSelected && 'fc-event-selected',
		data?.isDraggable && 'fc-event-draggable',
		(data?.isStartResizable || data?.isEndResizable) && 'fc-event-resizable',
		data?.event?.extendedProps?.isTimeOff && 'fc-time-off-event'
	);

/** Spread onto <FullCalendar> in Schedule + GameMaster. */
export const fullCalendarLegacyClassOptions = {
	/** Restores root `.fc` used widely in GameMaster SCSS */
	className: 'fc',
	viewClass: 'fc-view',
	views: {
		resourceTimeline: {
			className: 'fc-timeline fc-resource-timeline',
		},
		resourceTimelineDay: {
			className: 'fc-timeline fc-resource-timeline',
		},
		resourceTimelineWeek: {
			className: 'fc-timeline fc-resource-timeline fc-view-resourceTimelineWeek',
		},
	},
	tableClass: 'fc-scrollgrid fc-theme-standard',
	tableHeaderClass: (data: { isSticky?: boolean }) =>
		joinFcClass(
			'fc-scrollgrid-section-header',
			data?.isSticky && 'fc-scrollgrid-section-sticky'
		),
	tableBodyClass: 'fc-scrollgrid-section-body fc-timeline-body fc-datagrid-body',
	slotHeaderRowClass: 'fc-timeline-header-row',
	slotHeaderClass,
	slotHeaderInnerClass:
		'fc-timeline-slot-cushion fc-timeline-slot-label-cushion fc-scrollgrid-sync-inner fc-col-header-cell-cushion',
	slotLaneClass,
	resourceColumnHeaderClass: 'fc-datagrid-header',
	resourceColumnHeaderInnerClass:
		'fc-datagrid-cell-cushion fc-datagrid-cell-main fc-scrollgrid-sync-inner',
	resourceCellClass: 'fc-datagrid-cell fc-resource fc-datagrid-cell-frame',
	resourceCellInnerClass: 'fc-datagrid-cell-cushion fc-datagrid-cell-main',
	resourceLaneClass: 'fc-timeline-lane fc-timeline-lane-frame',
	resourceRowClass: 'fc-resource fc-resource-timeline-row',
	eventClass: 'fc-event',
	eventInnerClass: 'fc-event-main',
	rowEventClass,
	rowEventInnerClass: 'fc-event-main',
	rowEventTimeClass: 'fc-event-time',
	rowEventTitleClass: 'fc-event-title',
	nowIndicatorLineClass: 'fc-now-indicator-line fc-timeline-now-indicator-line',
};
